/**
 * Atomic Fields - Reusable field configurations for Screen Editor
 *
 * Export all atomic field factories, options, and templates.
 */

import type { FieldConfig } from '../../types/editorFieldTypes';
import { V2_TEXT_ALIGN_OPTIONS } from './options';

/**
 * Remap V1 text style fields to V2 TypographyStyle:
 *   .styles.* → .appearance.*
 *   .appearance.textAlign → .appearance.align
 *   textAlign options: left/right → start/end
 */
export function remapTextFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    const isAlignField = path.endsWith('.appearance.textAlign');
    if (isAlignField) {
      path = path.replace('.appearance.textAlign', '.appearance.align');
      return { ...f, path, options: [...V2_TEXT_ALIGN_OPTIONS] };
    }
    return path !== f.path ? { ...f, path } : f;
  });
}

/**
 * Remap V1 button container style fields to V2 ButtonBlock:
 *   .styles.* → .appearance.*
 *   .appearance.borderWidth → .appearance.borders.width
 *   .appearance.borderColor → .appearance.borders.color
 *   .appearance.borderRadius → .appearance.borders.radius.uniform
 *   .appearance.height → .appearance.dimensions.height
 *   .appearance.width → .appearance.dimensions.width
 */
export function remapButtonStyleFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    if (path.endsWith('.appearance.borderWidth')) path = path.replace('.appearance.borderWidth', '.appearance.borders.width');
    else if (path.endsWith('.appearance.borderColor')) path = path.replace('.appearance.borderColor', '.appearance.borders.color');
    else if (path.endsWith('.appearance.borderRadius')) path = path.replace('.appearance.borderRadius', '.appearance.borders.radius.uniform');
    else if (path.endsWith('.appearance.height')) path = path.replace('.appearance.height', '.appearance.dimensions.height');
    else if (path.endsWith('.appearance.width')) path = path.replace('.appearance.width', '.appearance.dimensions.width');
    return path !== f.path ? { ...f, path } : f;
  });
}

// Options arrays
export {
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  TEXT_ALIGN_OPTIONS,
  NUMBER_0_50_OPTIONS,
  NUMBER_0_100_OPTIONS,
  NUMBER_0_500_OPTIONS,
  SCALE_MODE_OPTIONS,
  BOOLEAN_OPTIONS,
  CHECKBOX_KIND_OPTIONS,
  TEXT_KIND_OPTIONS,
  LINK_TYPE_OPTIONS,
  IMAGE_KIND_OPTIONS,
  TOP_ALIGNMENT_OPTIONS,
  IMAGE_VERTICAL_POSITION_OPTIONS,
  BUTTON_VISIBILITY_OPTIONS,
  V2_TEXT_ALIGN_OPTIONS,
  HORIZONTAL_ALIGNMENT_OPTIONS,
  ICON_PLACEMENT_OPTIONS,
} from './options';

// Text style field templates and factories
export {
  BUTTON_TEXT_STYLE_FIELDS,
  TITLE_TEXT_STYLE_FIELDS,
  V2_TITLE_TEXT_STYLE_FIELDS,
  PLACEHOLDER_TEXT_STYLE_FIELDS,
  createTextStyleFields,
  createV2AppearanceStyleFields,
  createButtonStyleFields,
  // Button container styles (ButtonBlock)
  BUTTON_CONTAINER_STYLE_FIELDS,
  createButtonContainerStyleFields,
  // V2-only button appearance (gradient, shadow, opacity, animation)
  V2_BUTTON_APPEARANCE_FIELDS,
  createV2ButtonAppearanceFields,
  // V2 icon fields (icon + iconPlacement)
  createV2IconFields,
  // Footer container styles (BasicFooterBlock)
  FOOTER_CONTAINER_STYLE_FIELDS,
  createFooterContainerStyleFields,
} from './textStyleFields';

// Background style field templates and factories
export {
  IMAGE_STYLE_FIELDS,
  VIDEO_STYLE_FIELDS,
  createImageStyleFields,
  createVideoStyleFields,
  // Extended image fields for body section images (includes width/height)
  EXTENDED_IMAGE_STYLE_FIELDS,
  createExtendedImageStyleFields,
  // Simple image fields for slider/list items (without width/height)
  SIMPLE_IMAGE_STYLE_FIELDS,
  createSimpleImageStyleFields,
  // List image fields with size controls (width/height for Table/TwoColumn items)
  LIST_IMAGE_STYLE_FIELDS,
  createListImageStyleFields,
} from './backgroundStyleFields';

// Media component style field templates and factories (for list screens)
export {
  MEDIA_IMAGE_STYLE_FIELDS,
  MEDIA_VIDEO_STYLE_FIELDS,
  createMediaImageStyleFields,
  createMediaVideoStyleFields,
} from './mediaStyleFields';

// LabelBlock style field templates and factories (for template text labels)
export {
  LABEL_BLOCK_STYLE_FIELDS,
  LABEL_BLOCK_PADDING_FIELDS,
  ALL_LABEL_BLOCK_FIELDS,
  createLabelBlockStyleFields,
  createAllLabelBlockFields,
} from './labelBlockFields';

// List style field factories (for list sections)
export {
  createListItemStateFields,
  createListCheckboxStyleFields,
  createListPositionFields,
} from './listStyleFields';

// PickerBlock style field templates and factories (for picker container)
export {
  VERTICAL_ALIGNMENT_OPTIONS,
  PICKER_BLOCK_STYLE_FIELDS,
  PICKER_BLOCK_PADDING_FIELDS,
  ALL_PICKER_BLOCK_FIELDS,
  createPickerBlockStyleFields,
  createAllPickerBlockFields,
} from './pickerStyleFields';
